import { CustomException } from '@tencent/ppd-base-libs';
import { ActState, codeAct as codeActSource, CodeAct as CodeActSource } from '@tencent/wphk-components-discount-code/business/codeAct';
import { CouponModule, couponModule } from '@tencent/wphk-components-discount-code/business/coupon';
import { getDiscountAward, init } from '@tencent/wphk-components-discount-code/business/index';
import { getDefaultDiscountCodeInfoByMrchId, getDiscountCodeInfoFromCfg } from '@tencent/wphk-components-discount-code/business/merchantCfg';
import { getUserInfo as getCodeActUserInfo } from '@tencent/wphk-components-discount-code/business/userInfo';
import { CouponState } from '@tencent/wphk-components-discount-code/types/index';
import { defineStore } from 'pinia';
import { toRefs } from 'vue';
import { getUrlParamByKey, UrlParamKey } from '../utils/url-params-helper';
import { useMerchantInfo } from './merchant-info-module';
/**
 * 活动信息
 */
interface CodeActInfo {
  /** url传入的优惠码 */
  codeFromUrl: string;
  /** 参与活动的优惠码（如果已参与活动的话） */
  awardCode: string;
  /** 活动参与状态 */
  actState: ActState;
  /** 当前状态是否可以切换优惠码, qry接口返回 */
  canSwitch: boolean;
}

/** 优惠码信息 */
type DiscountCodeInfo = {
  // 商户id
  mrchId: string,
  // 商户名
  mrchName: string,
  // 优惠码
  code: string,
  // 优惠码活动是否过期
  isExpired: boolean
};

/** 初始活动信息 */
const initActInfo: CodeActInfo = {
  codeFromUrl: '',
  awardCode: '',
  actState: ActState.CANNOTGOT,
  canSwitch: false,
};
/** 初始活动模块 */
const initCodeActSource: CodeActSource = codeActSource;
/** 初始券信息 */
const initCouponModule: CouponModule = couponModule;
/** 初始商户信息 */
const initMerchantInfo: DiscountCodeInfo = {
  mrchId: '',
  mrchName: '',
  code: '',
  isExpired: true,
};

/** 通用的优惠码新人活动 */
export const useNewUserStore = defineStore('newUser', {
  state: () => ({
    /** 活动信息 */
    actInfo: initActInfo,
    /** 公共组件中的活动模块 */
    actDetailInfo: initCodeActSource,
    /** 公共组件中的券信息模块 */
    couponModule: initCouponModule,
    /** 公共组件中的商户优惠码信息模块 */
    merchantInfo: initMerchantInfo,
  }),
  getters: {
    canGetAct(state): boolean {
      // 如果参与活动状态为：已参与，参与中，可参与，则true；不能参与则false
      return state.actInfo.actState !== ActState.CANNOTGOT;
    },
    isFoundCoupon(state): boolean {
      return !!state.couponModule.discountCodeCoupon.name;
    },
    /** 是否命中活动 - 判断优惠码配置、商户id是否存在，配置是否过期，用户是否 */
    isHit(state): boolean {
      if (!state.actInfo.codeFromUrl) {
        // 如果url中没有优惠码，则不命中
        return false;
      }
      // 是否从配置系统能找到对应的code(code === codeFromUrl)
      if (!state.merchantInfo.mrchId) {
        // 如果没有拿到配置系统的code,则不命中
        return false;
      }
      if (state.merchantInfo.isExpired) {
        // 如果该优惠码活动已过期，如果用户有参与过(awardCode不为空)这家商户(code相同)的优惠码活动，则命中。
        return !!state.actInfo.awardCode && state.actInfo.codeFromUrl === state.actInfo.awardCode;
      }
      return true;
    },
  },
  actions: {
    /** 初始化活动参数信息 */
    async initParams() {
      const codeFromUrl = getUrlParamByKey(UrlParamKey.discountCode);
      if (codeFromUrl) {
        this.actInfo.codeFromUrl = codeFromUrl;
        return;
      }
      // 如果url没有优惠码参数，则获取配置中对应商户的默认优惠码配置
      const { merchantConfig } = toRefs(useMerchantInfo());
      const merchantId = merchantConfig.value.mrchId;
      const defaultCodeCfg = await getDefaultDiscountCodeInfoByMrchId(merchantConfig.value.mrchId);
      let discountCode = defaultCodeCfg?.code || '';

      // https://tapd.woa.com/10145891/prong/stories/view/1010145891883953957
      // 特殊逻辑，针对url不带discountCode，但又需要迎新奖赏模块的商户，hack处理
      const DISCOUNT_CODE_20230601 = {
        'CM-1681201682952': 'WTS2023',
        'CM-1681201886400': 'PNS2023',
        'CM-1681202049537': 'FTS2023',
        'CM-1617696480669': 'SCANOFFER20',  // 双钱包活动-澳门711专属的discountCode
      };

      if (DISCOUNT_CODE_20230601[merchantId] && !discountCode) {
        discountCode = DISCOUNT_CODE_20230601[merchantId];
      }

      this.actInfo.codeFromUrl = discountCode;
    },
    async init() {
      await this.initParams();
      console.log('=====init newUserModule, codeFromUrl:', this.actInfo.codeFromUrl);
      // 如果url中没有优惠码参数
      if (!this.actInfo.codeFromUrl) {
        return false;
      }
      try {
        // 如果url中有优惠码参数
        await init(this.actInfo.codeFromUrl);
        // code是唯一的
        const discountCodeCfg = await getDiscountCodeInfoFromCfg(this.actInfo.codeFromUrl);
        this.merchantInfo = discountCodeCfg || this.merchantInfo;
        this.updateActInfo();
        if (!this.isFoundCoupon || !this.isHit || !this.isShow()) {
          // 如果没有找到对应券信息，或者没有命中迎新优惠码活动，则认为初始化失败
          return false;
        }
        console.log('===init discountCode success', this.actInfo);
        return true;
      } catch (e) {
        new CustomException(e, 'CommonNewUserInitFail', '新人优惠码初始化失败');
        throw e;
      }
    },
    /** 执行活动:领券 */
    async execAct() {
      if (this.canGetAct) {
        const { merchantConfig } = useMerchantInfo();
        await getDiscountAward(this.actInfo.codeFromUrl, {
          spId: merchantConfig.spId,
        });
        this.updateActInfo();
      }
    },
    /** 是否可以看到活动 */
    isShow() {
      // 如果券已过期/已失效，不展示
      const couponState = this.couponModule.discountCodeCoupon.state;
      const isBiNewUser = getCodeActUserInfo();
      // 如果用户已领取优惠码A，又打开了优惠码B页面：如果还是BI新用户，则展示；如果不是，则不展示
      const isSameCode = this.actInfo.codeFromUrl === this.actInfo.awardCode;
      return isSameCode ? [CouponState.notReceived, CouponState.received].includes(couponState) : isBiNewUser;
    },
    updateActInfo() {
      this.actInfo.actState = this.actDetailInfo.getActState();
      this.actInfo.awardCode = this.actDetailInfo.awardCode;
      this.actInfo.canSwitch = this.actDetailInfo.canSwitch;
    },
  },
});
